import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MfaMethod } from '@vivport/backend/types';
import { KeyRound, Mail, Shield } from 'lucide-react';

interface LoginMfaMethodSelectorProps {
  mfaMethods: MfaMethod[];
  onMethodSelect: (method: MfaMethod) => void;
  onBack?: () => void;
  isLoading?: boolean;
}

export function LoginMfaMethodSelector({ mfaMethods, onMethodSelect, onBack, isLoading = false }: LoginMfaMethodSelectorProps) {
  const hasPasskey = mfaMethods.includes('FIDO2');
  const hasEmail = mfaMethods.includes('EMAIL');

  // UI can render multiple methods, but backend enforces one active MFA mode per account.
  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <div className="flex items-center justify-center mb-4">
          <Shield className="h-12 w-12" />
        </div>
        <CardTitle className="text-center">Two-Factor Authentication</CardTitle>
        <CardDescription className="text-center">
          Choose your preferred verification method to continue
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {hasPasskey && (
            <Button 
              onClick={() => onMethodSelect('FIDO2')}
              disabled={isLoading}
              className="w-full h-auto p-4 flex items-center justify-start gap-4"
              variant="outline"
            >
              <KeyRound className="h-6 w-6" />
              <div className="text-left">
                <p className="font-medium">Passkey</p>
                <p className="text-sm text-muted-foreground">
                  Use your device's biometrics or security key
                </p>
              </div>
            </Button>
          )}

          {hasEmail && (
            <Button 
              onClick={() => onMethodSelect('EMAIL')}
              disabled={isLoading}
              className="w-full h-auto p-4 flex items-center justify-start gap-4"
              variant="outline"
            >
              <Mail className="h-6 w-6" />
              <div className="text-left">
                <p className="font-medium">Email Code</p>
                <p className="text-sm text-muted-foreground">
                  Receive a verification code via email
                </p>
              </div>
            </Button>
          )}

          {onBack && (
            <Button
              variant="ghost"
              onClick={onBack}
              disabled={isLoading}
              className="w-full mt-4"
            >
              Back to Login
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
} 